import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useAuth0Data } from '../../../context/Auth0DataContext';

const { FiPlus, FiSave, FiX, FiUser, FiCalendar, FiTarget, FiFileText, FiInfinity, FiRefreshCw, FiCheck } = FiIcons;

const CreateProject = () => {
  const { addItem, getAllUsers } = useAuth0Data();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    owner: '',
    dueDate: '',
    status: 'planning',
    priority: 'medium',
    ongoing: false
  });
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    assignee: '',
    status: 'todo'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const allUsers = getAllUsers();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in the required fields (Title and Description)');
      return;
    }

    if (!formData.owner.trim()) {
      setError('Please select a project owner');
      return;
    }

    // Validate due date for non-ongoing projects
    if (!formData.ongoing && !formData.dueDate) {
      setError('Please set a due date or mark the project as ongoing');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      console.log('🔄 Creating new project:', {
        title: formData.title,
        owner: formData.owner,
        status: formData.status,
        ongoing: formData.ongoing,
        tasksCount: tasks.length
      });

      const project = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        owner: formData.owner,
        dueDate: formData.ongoing ? null : formData.dueDate,
        status: formData.status,
        priority: formData.priority,
        tasks: tasks.map(task => ({
          ...task,
          id: task.id || Date.now() + Math.random(), // Ensure unique IDs
          status: task.status || 'todo'
        }))
      };

      console.log('💾 Saving project to Supabase:', project);
      
      await addItem('projects', project);
      
      console.log('✅ Project created successfully');

      // Reset form
      setFormData({
        title: '',
        description: '',
        owner: '',
        dueDate: '',
        status: 'planning',
        priority: 'medium',
        ongoing: false
      });
      setTasks([]);
      
      setSuccess('Project created successfully! 🎉');
      setTimeout(() => setSuccess(''), 5000);

    } catch (error) {
      console.error('❌ Error creating project:', error);
      setError(`Failed to create project: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTask = () => {
    if (newTask.title.trim() && newTask.assignee.trim()) {
      const task = {
        ...newTask,
        id: Date.now() + Math.random(), // Ensure unique ID
        title: newTask.title.trim(),
        assignee: newTask.assignee.trim(),
        status: newTask.status || 'todo'
      };
      
      setTasks(prev => [...prev, task]);
      setNewTask({ title: '', assignee: '', status: 'todo' });
    }
  };

  const removeTask = (taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      owner: '',
      dueDate: '',
      status: 'planning',
      priority: 'medium',
      ongoing: false
    });
    setTasks([]);
    setError('');
    setSuccess('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return 'text-blue-600 bg-blue-100';
      case 'in-progress': return 'text-yellow-600 bg-yellow-100';
      case 'completed': return 'text-green-600 bg-green-100';
      case 'on-hold': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
    >
      <div className="flex items-center mb-6">
        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiPlus} className="w-5 h-5 text-green-600" />
        </div>
        <h3 className="ml-3 text-xl font-bold text-gray-900">Create New Project</h3>
      </div>

      {/* Status Messages */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start"
        >
          <SafeIcon icon={FiX} className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          {error}
        </motion.div>
      )}

      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-start"
        >
          <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          {success}
        </motion.div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Project Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <SafeIcon icon={FiTarget} className="w-4 h-4 inline mr-2" />
              Project Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, title: e.target.value }));
                if (error) setError('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Enter project title"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <SafeIcon icon={FiFileText} className="w-4 h-4 inline mr-2" />
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, description: e.target.value }));
                if (error) setError('');
              }}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Describe the project"
              required
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
              Project Owner *
            </label>
            <select
              value={formData.owner}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, owner: e.target.value }));
                if (error) setError('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              required
              disabled={isSubmitting}
            >
              <option value="">Select Owner</option>
              {allUsers.map(user => (
                <option key={user.id} value={user.name}>{user.name}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                <SafeIcon icon={formData.ongoing ? FiInfinity : FiCalendar} className="w-4 h-4 inline mr-2" />
                {formData.ongoing ? 'Ongoing Project' : 'Due Date'}
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ongoing"
                  checked={formData.ongoing}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    ongoing: e.target.checked,
                    dueDate: e.target.checked ? '' : prev.dueDate
                  }))}
                  className="mr-2 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  disabled={isSubmitting}
                />
                <label htmlFor="ongoing" className="text-sm text-gray-600 cursor-pointer">
                  Ongoing
                </label>
              </div>
            </div>
            {!formData.ongoing ? (
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, dueDate: e.target.value }));
                  if (error) setError('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required={!formData.ongoing}
                disabled={isSubmitting}
              />
            ) : (
              <div className="flex items-center justify-center h-10 px-3 py-2 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                <SafeIcon icon={FiInfinity} className="w-5 h-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-500">No end date - ongoing project</span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isSubmitting}
            >
              <option value="planning">Planning</option>
              <option value="in-progress">In Progress</option>
              <option value="on-hold">On Hold</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isSubmitting}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        {/* Project Preview */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Project Preview</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(formData.status)}`}>
                {formData.status}
              </span>
            </div>
            <div>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(formData.priority)}`}>
                {formData.priority} priority
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {formData.ongoing ? (
                <div className="flex items-center">
                  <SafeIcon icon={FiInfinity} className="w-4 h-4 mr-1" />
                  Ongoing project
                </div>
              ) : (
                `Due: ${formData.dueDate || 'Not set'}`
              )}
            </div>
          </div>
        </div>

        {/* Tasks Section */}
        <div>
          <h4 className="font-medium text-gray-900 mb-4">Project Tasks (Optional)</h4>
          
          {/* Add Task Form */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Task title"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <select
                  value={newTask.assignee}
                  onChange={(e) => setNewTask(prev => ({ ...prev, assignee: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={isSubmitting}
                >
                  <option value="">Select Assignee</option>
                  {allUsers.map(user => (
                    <option key={user.id} value={user.name}>{user.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <button
                  type="button"
                  onClick={addTask}
                  disabled={!newTask.title.trim() || !newTask.assignee.trim() || isSubmitting}
                  className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
                  Add Task
                </button>
              </div>
            </div>
          </div>

          {/* Tasks List */}
          {tasks.length > 0 && (
            <div className="space-y-2">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                >
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{task.title}</h5>
                    <p className="text-sm text-gray-600">Assigned to: {task.assignee}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTask(task.id)}
                    disabled={isSubmitting}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex gap-4 pt-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />
                Creating Project...
              </>
            ) : (
              <>
                <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                Create Project
              </>
            )}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isSubmitting}
            className="flex items-center px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50"
          >
            <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
            Reset Form
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default CreateProject;